import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getJobOpenings } from "../api/recruitmentApi";
import { formatDate, normalizeList } from "./hrUtils";

import "./HRShared.css";
import "./ClosedHiring.css";

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Unable to load closed hiring records.";

const calculateDaysTaken = (startDate, endDate) => {
  if (!startDate || !endDate) return "-";

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "-";
  }

  const difference = end.getTime() - start.getTime();

  if (difference < 0) return "-";

  return Math.ceil(difference / 86400000);
};

const getClosedByName = (item) => {
  const closedBy = item?.closedBy;

  if (typeof closedBy === "string") {
    return closedBy;
  }

  return closedBy?.name || closedBy?.fullName || item?.closedByName || "-";
};

const getCurrentQuarter = (date = new Date()) =>
  Math.floor(date.getMonth() / 3) + 1;

const isDateInSelectedPeriod = (dateValue, period) => {
  if (!dateValue) return false;

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  const now = new Date();

  if (period === "month") {
    return (
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    );
  }

  if (period === "year") {
    return date.getFullYear() === now.getFullYear();
  }

  if (period === "quarter") {
    return (
      date.getFullYear() === now.getFullYear() &&
      getCurrentQuarter(date) === getCurrentQuarter(now)
    );
  }

  if (period === "tillDate") {
    return date <= now;
  }

  return true;
};

const ClosedHiring = () => {
  const now = new Date();

  const [period, setPeriod] = useState("month");
  const [department, setDepartment] = useState("");

  const [items, setItems] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const currentMonthName = now.toLocaleString("en-US", {
    month: "long",
  });

  const currentYear = now.getFullYear();
  const currentQuarter = getCurrentQuarter(now);

  const periodLabel = useMemo(() => {
    if (period === "month") {
      return `${currentMonthName} ${currentYear}`;
    }

    if (period === "year") {
      return `January to ${currentMonthName} ${currentYear}`;
    }

    if (period === "quarter") {
      return `Q${currentQuarter} ${currentYear}`;
    }

    return `Till ${currentMonthName} ${currentYear}`;
  }, [period, currentMonthName, currentQuarter, currentYear]);

  const loadDepartments = useCallback(async () => {
    try {
      const response = await getJobOpenings({
        status: "Closed",
        limit: 1000,
      });

      const list = normalizeList(response);

      const departments = [
        ...new Set(
          list
            .map((item) => String(item?.department || "").trim())
            .filter(Boolean),
        ),
      ].sort((first, second) => first.localeCompare(second));

      setDepartmentOptions(departments);
    } catch (requestError) {
      console.error("Unable to load departments:", requestError);
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      /*
       * Sab closed jobs load kar rahe hain.
       * Current Month, Year aur Quarter filtering frontend me
       * exact actualClosingDate se hogi.
       */
      const response = await getJobOpenings({
        status: "Closed",
        department: department || undefined,
        limit: 1000,
      });

      const list = normalizeList(response);

      const filteredRecords = list.filter((item) => {
        const isClosed =
          String(item?.status || "")
            .trim()
            .toLowerCase() === "closed";

        if (!isClosed) {
          return false;
        }

        if (
          department &&
          String(item?.department || "")
            .trim()
            .toLowerCase() !== department.trim().toLowerCase()
        ) {
          return false;
        }

        return isDateInSelectedPeriod(item?.actualClosingDate, period);
      });

      setItems(filteredRecords);
    } catch (requestError) {
      console.error("Unable to load closed hiring:", requestError);

      setItems([]);
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [department, period]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const totalRequired = useMemo(
    () =>
      items.reduce(
        (total, item) => total + Number(item?.numberOfPositions || 0),
        0,
      ),
    [items],
  );

  const totalHired = useMemo(
    () =>
      items.reduce((total, item) => total + Number(item?.hiredCount || 0), 0),
    [items],
  );

  return (
    <div className="hr-page">
      <header className="hr-page-header">
        <div className="hr-page-heading">
          <h1>Closed Hiring</h1>

          <p>
            Review positions successfully closed during the selected period.
          </p>
        </div>
      </header>

      <section className="closed-hiring-filter-card">
        <div className="closed-hiring-filter-title">Filters</div>

        <div className="closed-hiring-filter-content">
          <div className="closed-hiring-filter-field">
            <label>Department</label>

            <select
              className="hr-select"
              value={department}
              onChange={(event) => setDepartment(event.target.value)}
            >
              <option value="">All departments</option>

              {departmentOptions.map((departmentName) => (
                <option key={departmentName} value={departmentName}>
                  {departmentName}
                </option>
              ))}
            </select>
          </div>

          <div className="closed-hiring-filter-field closed-hiring-period-field">
            <label>Date Range</label>

            <div className="closed-hiring-period-buttons">
              <button
                type="button"
                className={
                  period === "month"
                    ? "closed-period-btn active"
                    : "closed-period-btn"
                }
                onClick={() => setPeriod("month")}
              >
                Current Month
              </button>

              <button
                type="button"
                className={
                  period === "quarter"
                    ? "closed-period-btn active"
                    : "closed-period-btn"
                }
                onClick={() => setPeriod("quarter")}
              >
                Current Quarter
              </button>

              <button
                type="button"
                className={
                  period === "year"
                    ? "closed-period-btn active"
                    : "closed-period-btn"
                }
                onClick={() => setPeriod("year")}
              >
                Current Year
              </button>

              <button
                type="button"
                className={
                  period === "tillDate"
                    ? "closed-period-btn active"
                    : "closed-period-btn"
                }
                onClick={() => setPeriod("tillDate")}
              >
                Till Date
              </button>
            </div>

            <div className="closed-hiring-showing-text">
              Showing: <strong>{periodLabel}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="hr-summary-grid">
        <div className="hr-summary-card">
          <span>Closed Positions</span>
          <strong>{items.length}</strong>
          <small>{periodLabel}</small>
        </div>

        <div className="hr-summary-card">
          <span>Required Employees</span>
          <strong>{totalRequired}</strong>
          <small>Total approved positions</small>
        </div>

        <div className="hr-summary-card">
          <span>Employees Hired</span>
          <strong>{totalHired}</strong>
          <small>Successfully joined candidates</small>
        </div>
      </section>

      {error && (
        <div className="hr-error">
          <span>{error}</span>

          <button type="button" className="hr-error-retry" onClick={loadItems}>
            Retry
          </button>
        </div>
      )}

      <div className="hr-table-wrap">
        <table className="hr-table">
          <thead>
            <tr>
              <th>Position</th>
              <th>Department</th>
              <th>Required</th>
              <th>Hired</th>
              <th>Opened On</th>
              <th>Closed On</th>
              <th>Days Taken</th>
              <th>Closed By</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="hr-empty">
                  Loading closed hiring records...
                </td>
              </tr>
            ) : items.length > 0 ? (
              items.map((item) => (
                <tr key={item._id}>
                  <td>
                    <strong>{item?.jobTitle || "Untitled Position"}</strong>
                  </td>

                  <td>{item?.department || "-"}</td>

                  <td>{Number(item?.numberOfPositions || 0)}</td>

                  <td>
                    <span className="hr-closed-hired-count">
                      {Number(item?.hiredCount || 0)}
                    </span>
                  </td>

                  <td>{formatDate(item?.openingDate)}</td>

                  <td>{formatDate(item?.actualClosingDate)}</td>

                  <td>
                    {calculateDaysTaken(
                      item?.openingDate,
                      item?.actualClosingDate,
                    )}
                  </td>

                  <td>{getClosedByName(item)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="hr-empty">
                  No hiring was closed for {periodLabel}
                  {department ? ` in ${department}.` : "."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClosedHiring;
